import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import { Form } from 'react-final-form'
import * as yup from 'yup'
import { find, values } from 'lodash'
import { useTranslation } from 'react-i18next'
import {
  makeStyles,
  Modal,
  Typography,
  TextField,
  Link,
  Select,
  MenuItem,
  Box,
} from '@material-ui/core'
import Button from 'components/Button'
import MainColumn from 'components/MainColumn'
import { FieldSelect, FieldText } from 'components/Fields'
import { validateFormValues } from 'utils/forms'
import Filters from 'components/FilterList/Filters'
import Pagination from 'components/Pagination'
import clsx from 'clsx'
import { useSelector, useDispatch } from 'react-redux'
import {
  getDadosAuditoriaSelecionada,
  getParecerDesempenhoBiodiverdidadeSelecionado,
  getListaParecerDesempenhoBiodiverdidade,
  getListaProjetosEmpresa,
  getIsSavingParecerDesempenhoBiodiverdidade,
} from 'store/modules/auditor/auditor.selectors'
import { callSalvarParecerDesempenho } from 'store/modules/auditor'
import {
  comboDesempenhoComentarSobre,
  comboDesempenhoOcorrenciaIrcProjeto,
  comboDesempenhoOcorrenciaPontuacaoProjeto,
  comboDesempenhoOcorrenciaProjeto,
  comboDesempenhoTipoComentario,
  comboDesempenhoOcorrenciaAcaoProjeto,
  comboDesempenhoOcorrenciaClassificacaoAcaoProjeto,
  comboDesempenhoOcorrenciaQualificadoresAcaoProjeto,
} from 'utils/constantes'
import {
  apiListarAuditoresEmpresa,
  apiSalvarEditarAuditor,
} from 'store/modules/api/auditor.actions'

const useStyles = makeStyles(theme => ({
  withoutResults: {
    fontSize: '32px',
    fontWeight: '400',
    color: '#C9CEDB',
    padding: '30px 0',
    textAlign: 'center',
  },
  contentBox: {
    minHeight: '50vh',
  },
  maxInput: {
    maxWidth: '420px',
  },
  footerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  },
  search: {
    marginBottom: '32px',
  },
  listComments: {
    listStyle: 'none',
    padding: '32px 10px',
    borderTop: `1px solid ${theme.palette.grey.main}`,
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
  },
  lineComment: {
    display: 'flex',
    columnGap: '16px',
  },
  textComment: {
    fontSize: '14px',
    color: '#0F3444',
    whiteSpace: 'pre-line',
    margin: 0,
  },
  firstColumn: {
    minWidth: '180px',
  },
  pending: {
    displa: 'block',
    borderRadius: '100px',
    padding: '3px 12px',
    color: '#FFFFFF',
    backgroundColor: '#F85453',
  },
  concluded: {
    displa: 'block',
    borderRadius: '100px',
    padding: '3px 12px',
    color: '#FFFFFF',
    backgroundColor: '#219754',
  },
  pagination: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.grey.main}`,
  },
  modalContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3),
    boxShadow: theme.shadows[5],
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 400,
  },
}))

const TabDesempenhoBiodiversidade = forwardRef(({ controlAdded, setControlAdded }, ref) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()

  const dadosAuditoria = useSelector(getDadosAuditoriaSelecionada)
  const parecerSelecionado = useSelector(getParecerDesempenhoBiodiverdidadeSelecionado)
  const listaPareceres = useSelector(getListaParecerDesempenhoBiodiverdidade)
  const projetos = useSelector(getListaProjetosEmpresa)
  const isSaving = useSelector(getIsSavingParecerDesempenhoBiodiverdidade)
  const [page, setPage] = useState(0)
  const pageSize = 5
  const filterScreen = [
    { value: true, label: 'filtros.maisAntigos', name: 'time' },
    { value: false, label: 'filtros.maisRecentes', name: 'time' },
  ]

  const [editAuditor, setEditAuditor] = useState(false)
  const [auditorValue, setAuditorValue] = useState('')
  const [parecerId, setParecerId] = useState(null)
  const [auditorId, setAuditorId] = useState(null)
  const [auditorList, setAuditorList] = useState([])

  useImperativeHandle(ref, () => ({
    handleButtonAdded() {
      setControlAdded(true)
    },
  }))

  const handleSave = formValues => {
    const data = {
      ...formValues,
      auditoriaEmpresaId: dadosAuditoria.id,
    }

    dispatch(callSalvarParecerDesempenho(data)).then(() => {
      setControlAdded(false)
    })
  }

  const handleEditAuditor = parecer => {
    setAuditorValue(parecer.auditor)
    setParecerId(parecer.id)
    setAuditorId(parecer.usuarioId)
    setEditAuditor(true)
  }

  useEffect(() => {
    const fetchAuditores = async () => {
      if (dadosAuditoria && dadosAuditoria.empresaId) {
        const result = await dispatch(
          apiListarAuditoresEmpresa({
            empresaId: dadosAuditoria.empresaId,
          }),
        )
        setAuditorList(result.payload)
      }
    }

    fetchAuditores()
  }, [dispatch, dadosAuditoria])

  const handleSaveAuditor = () => {
    const body = {
      auditorId,
      parecerId,
      nome: auditorValue,
    }

    dispatch(apiSalvarEditarAuditor(body))

    setEditAuditor(false)
  }

  const [data, setData] = useState(listaPareceres)

  useEffect(() => {
    setData(listaPareceres)
  }, [listaPareceres])

  const filteredData = data
    .slice(page * pageSize, page * pageSize + pageSize)
    .map(item => ({ ...item }))

  const dinamicValidation = yup.object({
    auditor: yup.string().required(),
    tipoComentario: yup.string().required(),
    tipoOcorrencia: yup.string().required(),
    projetoId: yup.number().when('tipoOcorrencia', {
      is: val => val !== 'OUTRO',
      then: schema => schema.required(),
      otherwise: schema => schema.nullable(),
    }),
    campo: yup.string().when('tipoOcorrencia', {
      is: val => !['PONTUACAO_PROJETO', 'PONTUACAO_ACAO', 'OUTRO', 'DADOS_PROJETO'].includes(val),
      then: schema => schema.required(),
      otherwise: schema => schema.nullable(),
    }),
    validade: yup.string().required(),
    comentario: yup.string().required(),
    acaoId: yup.number().when('tipoOcorrencia', {
      is: val =>
        ['DADOS_ACAO', 'CLASSIFICACAO_ACAO', 'QUALIFICADORES_ACAO', 'PONTUACAO_ACAO'].includes(val),
      then: schema => schema.required(),
      otherwise: schema => schema.nullable(),
    }),
    qualificadorId: yup.number().when('tipoOcorrencia', {
      is: val => val === 'QUALIFICADORES_ACAO',
      then: schema => schema.required(),
      otherwise: schema => schema.nullable(),
    }),
    tipoPontuacao: yup.string().when('tipoOcorrencia', {
      is: val => val === 'PONTUACAO_PROJETO',
      then: schema => schema.required(),
      otherwise: schema => schema.nullable(),
    }),
  })

  const limparDadosAlteracaoProjeto = form => {
    form.change('acaoId', null)
    form.change('qualificadorId', null)
    form.change('campo', '')
  }

  const limparDadosAlteracaoTipoOcorrencia = form => {
    form.change('acaoId', null)
    form.change('qualificadorId', null)
    form.change('campo', '')
    form.change('projetoId', null)
    form.change('tipoPontuacao', null)
  }

  const getNomeCampo = parecer => {
    switch (parecer.tipoOcorrencia) {
      case 'DADOS_PROJETO':
        return t(find(comboDesempenhoOcorrenciaProjeto, { id: parecer.campo })?.label)
      case 'PONTUACAO_PROJETO':
        return t(find(comboDesempenhoOcorrenciaPontuacaoProjeto, { id: parecer.campo })?.label)
      case 'IRC_PROJETO':
        return t(find(comboDesempenhoOcorrenciaIrcProjeto, { id: parecer.campo })?.label)
      case 'DADOS_ACAO':
        return t(find(comboDesempenhoOcorrenciaAcaoProjeto, { id: parecer.campo })?.label)
      case 'CLASSIFICACAO_ACAO':
        return t(
          find(comboDesempenhoOcorrenciaClassificacaoAcaoProjeto, { id: parecer.campo })?.label,
        )
      case 'QUALIFICADORES_ACAO':
        return t(
          find(comboDesempenhoOcorrenciaQualificadoresAcaoProjeto, { id: parecer.campo })?.label,
        )
      default:
        return ''
    }
  }
  const isEditing = Boolean(parecerSelecionado)
  return (
    <div className={classes.contentBox}>
      <div className={classes.search}>
        <Filters
          fullWidth
          items={listaPareceres}
          setItems={setData}
          origin={{ vertical: 'top', horizontal: 'right' }}
          filterScreen={filterScreen}
          placeholder={`${data.length} ${t('inputs.comentarios')}`}
          disabled
        />
      </div>

      {controlAdded && (
        <Form
          key={Date.now()}
          onSubmit={handleSave}
          validate={validateFormValues(dinamicValidation)}
          initialValues={{
            auditor: parecerSelecionado?.auditor ?? '',
            tipoComentario: parecerSelecionado?.tipoComentario ?? '',
            tipoOcorrencia: parecerSelecionado?.tipoOcorrencia ?? '',
            projetoId: parecerSelecionado?.projetoId ?? null,
            acaoId: parecerSelecionado?.acaoId ?? null,
            qualificadorId: parecerSelecionado?.qualificadorId ?? null,
            campo: parecerSelecionado?.campo ?? '',
            validade: parecerSelecionado?.validade ?? '',
            comentario: parecerSelecionado?.comentario ?? '',
            tipoPontuacao: parecerSelecionado?.tipoPontuacao ?? '',
          }}
          render={({ handleSubmit, values: formValues, form, pristine, valid }) => (
            <form onSubmit={handleSubmit}>
              <MainColumn>
                <FieldText
                  required
                  name='auditor'
                  label={t('inputs.auditorResponsavel')}
                  className={classes.maxInput}
                />
                <FieldSelect
                  required
                  id='tipoComentario'
                  name='tipoComentario'
                  label={t('inputs.tipoComentario')}
                  options={comboDesempenhoTipoComentario.map(option => ({
                    label: t(option.label),
                    value: option.id,
                  }))}
                  className={classes.maxInput}
                />

                <FieldSelect
                  required
                  id='tipoOcorrencia'
                  name='tipoOcorrencia'
                  label={t('inputs.comentarSobre')}
                  options={comboDesempenhoComentarSobre.map(option => ({
                    label: t(option.label),
                    value: option.id,
                  }))}
                  className={classes.maxInput}
                  onChange={() => limparDadosAlteracaoTipoOcorrencia(form)}
                />
                {formValues.tipoOcorrencia !== 'OUTRO' && (
                  <FieldSelect
                    required
                    id='projetoId'
                    name='projetoId'
                    label={t('inputs.qualProjeto')}
                    options={(projetos || []).map(option => ({
                      label: option.nome,
                      value: option.id,
                    }))}
                    className={classes.maxInput}
                    onChange={() => limparDadosAlteracaoProjeto(form)}
                  />
                )}
                {formValues.tipoOcorrencia === 'PONTUACAO_PROJETO' && (
                  <FieldSelect
                    required
                    id='tipoPontuacao'
                    name='tipoPontuacao'
                    label={t('inputs.qualPontuacao')}
                    options={(comboDesempenhoOcorrenciaPontuacaoProjeto || []).map(option => ({
                      label: t(option.label),
                      value: option.id,
                    }))}
                    className={classes.maxInput}
                  />
                )}
                {formValues.tipoOcorrencia === 'DADOS_PROJETO' && (
                  <FieldSelect
                    id='campo'
                    name='campo'
                    label={t('inputs.campoItem')}
                    options={comboDesempenhoOcorrenciaProjeto.map(option => ({
                      label: t(option.label),
                      value: option.id,
                    }))}
                    className={classes.maxInput}
                  />
                )}
                {formValues.tipoOcorrencia === 'IRC_PROJETO' && (
                  <FieldSelect
                    required
                    id='campo'
                    name='campo'
                    label={t('inputs.campoItem')}
                    options={comboDesempenhoOcorrenciaIrcProjeto.map(option => ({
                      label: t(option.label),
                      value: option.id,
                    }))}
                    className={classes.maxInput}
                  />
                )}

                {[
                  'DADOS_ACAO',
                  'CLASSIFICACAO_ACAO',
                  'QUALIFICADORES_ACAO',
                  'PONTUACAO_ACAO',
                ].includes(formValues.tipoOcorrencia) && (
                  <FieldSelect
                    required
                    id='acaoId'
                    name='acaoId'
                    label={t('inputs.qualAcao')}
                    options={find(projetos, { id: formValues.projetoId })?.acoes?.map(option => ({
                      label: option.descricao,
                      value: option.id,
                    }))}
                    className={classes.maxInput}
                  />
                )}

                {formValues.tipoOcorrencia === 'DADOS_ACAO' && (
                  <FieldSelect
                    required
                    id='campo'
                    name='campo'
                    label={t('inputs.campoItem')}
                    options={comboDesempenhoOcorrenciaAcaoProjeto.map(option => ({
                      label: t(option.label),
                      value: option.id,
                    }))}
                    className={classes.maxInput}
                  />
                )}

                {formValues.tipoOcorrencia === 'CLASSIFICACAO_ACAO' && (
                  <FieldSelect
                    required
                    id='campo'
                    name='campo'
                    label={t('inputs.campoItem')}
                    options={comboDesempenhoOcorrenciaClassificacaoAcaoProjeto.map(option => ({
                      label: t(option.label),
                      value: option.id,
                    }))}
                    className={classes.maxInput}
                  />
                )}

                {formValues.tipoOcorrencia === 'QUALIFICADORES_ACAO' && (
                  <>
                    <FieldSelect
                      required
                      id='qualificadorId'
                      name='qualificadorId'
                      label={t('inputs.qualQualificador')}
                      options={find(find(projetos, { id: formValues.projetoId })?.acoes, {
                        id: formValues.acaoId,
                      })
                        ?.acaoQualificadores?.map(acaoQualificador => acaoQualificador.qualificador)
                        ?.map(qualificador => ({
                          label: `${qualificador.identificador} - ${qualificador.nome}`,
                          value: qualificador.id,
                        }))}
                      className={classes.maxInput}
                    />
                    <FieldSelect
                      required
                      id='campo'
                      name='campo'
                      label={t('inputs.campoItem')}
                      options={comboDesempenhoOcorrenciaQualificadoresAcaoProjeto.map(option => ({
                        label: t(option.label),
                        value: option.id,
                      }))}
                      className={classes.maxInput}
                    />
                  </>
                )}

                <FieldText
                  required
                  name='validade'
                  label={t('inputs.validadeEmDias')}
                  type='number'
                  className={classes.maxInput}
                />
                <FieldText
                  required
                  name='comentario'
                  label={t('inputs.comentarioAuditoria')}
                  multiline
                  minRows={5}
                />
              </MainColumn>
              <div className={classes.footerSection}>
                <Button variant='danger' onClick={() => setControlAdded(false)}>
                  {t('inputs.cancelar')}
                </Button>

                <Button disabled={pristine || !valid} loading={isSaving} type='submit'>
                  {t('inputs.salvar')}
                </Button>
              </div>
            </form>
          )}
        />
      )}

      {data.length < 1 ? (
        <Typography component='h1' className={classes.withoutResults}>
          {t('auditoriaEmpresa.voceAindaNaoFezComentarios')}
        </Typography>
      ) : (
        <>
          {filteredData.map((parecer, index) => (
            <ul className={classes.listComments} key={index}>
              <li className={classes.lineComment}>
                <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                  {t('auditoriaEmpresa.auditorResponsavel')}
                </h3>
                <p className={classes.textComment}>{parecer.auditor}</p>
                <Button xSmall onClick={() => handleEditAuditor(parecer)}>
                  {t('editar')}
                </Button>
              </li>
              <li className={classes.lineComment}>
                <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                  {t('auditoriaEmpresa.tipoComentario')}
                </h3>
                <p className={classes.textComment}>
                  {t(find(comboDesempenhoTipoComentario, { id: parecer.tipoComentario })?.label)}
                </p>
              </li>
              <li className={classes.lineComment}>
                <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                  {t('auditoriaEmpresa.comentarioSobre')}
                </h3>
                <p className={classes.textComment}>
                  {t(find(comboDesempenhoComentarSobre, { id: parecer.tipoOcorrencia })?.label)}
                </p>
              </li>
              {parecer.tipoOcorrencia !== 'OUTRO' && (
                <li className={classes.lineComment}>
                  <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                    {t('auditoriaEmpresa.qualProjeto')}
                  </h3>
                  <p className={classes.textComment}>{parecer.projeto?.nome}</p>
                </li>
              )}

              {parecer.tipoOcorrencia === 'PONTUACAO_PROJETO' && (
                <li className={classes.lineComment}>
                  <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                    {t('auditoriaEmpresa.qualPontuacao')}
                  </h3>
                  <p className={classes.textComment}>
                    {t(
                      find(comboDesempenhoOcorrenciaPontuacaoProjeto, { id: parecer.tipoPontuacao })
                        ?.label,
                    )}
                  </p>
                </li>
              )}

              {parecer.acaoId && (
                <li className={classes.lineComment}>
                  <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                    {t('auditoriaEmpresa.qualAcao')}
                  </h3>
                  <p className={classes.textComment}>{parecer.acao?.descricao}</p>
                </li>
              )}

              {parecer.qualificadorId && (
                <li className={classes.lineComment}>
                  <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                    {t('auditoriaEmpresa.qualQualificador')}
                  </h3>
                  <p
                    className={
                      classes.textComment
                    }>{`${parecer.qualificador?.identificador} ${parecer.qualificador.nome}`}</p>
                </li>
              )}
              {!['PONTUACAO_PROJETO', 'PONTUACAO_ACAO', 'OUTRO'].includes(
                parecer.tipoOcorrencia,
              ) && (
                <li className={classes.lineComment}>
                  <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                    {t('auditoriaEmpresa.campoItem')}
                  </h3>
                  <p className={classes.textComment}>{getNomeCampo(parecer)}</p>
                </li>
              )}
              <li className={classes.lineComment}>
                <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                  {t('auditoriaEmpresa.validadeDias')}
                </h3>
                <p className={classes.textComment}>{parecer.validade}</p>
              </li>
              <li className={classes.lineComment}>
                <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                  {t('auditoriaEmpresa.comentario')}
                </h3>
                <p className={classes.textComment}>{parecer.comentario}</p>
              </li>
              <li className={classes.lineComment}>
                <h3 className={clsx(classes.textComment, classes.firstColumn)}>
                  {t('auditoriaEmpresa.status')}
                </h3>
                <p
                  className={clsx(
                    classes.textComment,
                    parecer.resolvido ? classes.concluded : classes.pending,
                  )}>
                  {t(`auditoriaEmpresa.${parecer.resolvido ? 'concluido' : 'pendente'}`)}
                </p>
              </li>
            </ul>
          ))}
          <div className={classes.pagination}>
            {!!filteredData.length && (
              <Pagination page={page} onChange={setPage} pageSize={pageSize} count={data.length} />
            )}
          </div>
        </>
      )}
      <Modal open={editAuditor} onClose={() => setEditAuditor(false)}>
        <div className={classes.modalContent}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography
              style={{
                color: 'rgba(0, 0, 0, 0.54)',
                fontSize: '0.8rem',
              }}>
              {t('inputs.EnviarEmailsPara')}
            </Typography>
            <Select
              value={auditorId}
              onChange={e => {
                setAuditorId(e.target.value)
              }}>
              {auditorList.length > 0 &&
                auditorList?.map(auditor => (
                  <MenuItem key={auditor.id} value={auditor.id}>
                    {auditor.nome}
                  </MenuItem>
                ))}
            </Select>
          </Box>
          <TextField
            label={t('inputs.auditorResponsavel')}
            value={auditorValue}
            onChange={e => setAuditorValue(e.target.value)}
          />
          <Button onClick={handleSaveAuditor}>{t('inputs.salvar')}</Button>
        </div>
      </Modal>
    </div>
  )
})

TabDesempenhoBiodiversidade.propTypes = {
  controlAdded: PropTypes.bool,
  setControlAdded: PropTypes.func,
}

export default TabDesempenhoBiodiversidade
